import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { retry, skipWhile, switchMap, take, tap, catchError } from 'rxjs/operators';
import { AppConfiguration } from '../classes/configuration';
import { Profile } from '../classes/profile';
import { ProfileUpdate } from '../classes/profile-update';
import { IMPERSONATION_KEY, PROFILE_RETRY_COUNT } from '../common/constants';
import { ConfigurationService } from './configuration.service';
import { LoggerService } from './logger.service';
import { RolesService } from './roles.service';
import { ToastService } from './toast.service';
import { MsCal } from '../classes/ms-cal';

// FOR TEST
// const profileUrlGET = "/assets/data/mock-data/userProfile.json";

@Injectable({
  providedIn: 'root'
})
export class ProfileService implements OnDestroy {
  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private toastService: ToastService,
    private rolesService: RolesService,
    private configurationService: ConfigurationService,
    private router: Router
  ) {}
  unsubscribe$: Subject<boolean> = new Subject();
  public profileObs$: BehaviorSubject<Profile> = new BehaviorSubject(null);

  getProfileObs(): Observable<Profile> {
    return this.profileObs$.asObservable();
  }

  setProfileObs(profile: Profile): void {
    this.profileObs$.next(profile);
  }

  getLatestProfileOnce(): Observable<Profile> {
    return this.profileObs$.pipe(skipWhile((profile: Profile) => !profile), take(1));
  }

  getSavedAssignedTasks(): any {
    let taskIds: string[] = [];
    if (typeof localStorage.savedStartedTasks !== 'undefined' && localStorage.savedStartedTasks !== null) {
      try {
        taskIds = JSON.parse(localStorage.savedStartedTasks);
      } catch (e) {
        this.logger.error('error caught processing savedStartedTasks');
      }
    }
    return { taskIds };
  }

  postProfile(): Observable<HttpResponse<any>> {
    const savedStartedTasks = this.getSavedAssignedTasks();
    this.logger.debug(`Saved Tasks Found: `, savedStartedTasks, 'http');

    return this.configurationService.getConfiguration().pipe(
      skipWhile((config: AppConfiguration) => !config),
      take(1),
      switchMap((config: AppConfiguration) =>
        this.http.post<HttpResponse<any>>(config.HostApi.concat('/profile/userProfile'), savedStartedTasks, {
          observe: 'response'
        })
      ),
      tap(
        (resp) => {
          const _resp: HttpResponse<any> = resp;
          let profile: Profile = _resp.body;
          if (profile) {
            if(IMPERSONATION_KEY in localStorage){
              let profileDeepCopy = Object.assign({}, this.setImpersonationOnLoad(profile));
              this.setProfileObs(profileDeepCopy);
              this.rolesService.setRoles(profileDeepCopy.userRoles);
            }else{
              this.setProfileObs(profile);
              this.rolesService.setRoles(profile.userRoles);
            }
            this.logger.debug(`POST Profile response: ${resp.status} ${resp.statusText}`, resp, 'http');
          }
        }
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      retry(PROFILE_RETRY_COUNT)
    );
  }

  /*
     -There is a similar method for this used in settings.service.
     -The method below will set the role on app load.
     -The next page refresh will delete the localStorage key and the UI will return to using ERD roles.
  */
  setImpersonationOnLoad(profile: Profile): Profile{
    const impersonate_role = localStorage.getItem(IMPERSONATION_KEY)
    this.logger.debug(`cvv_imp_rl found: ${impersonate_role}`, 'auth');
    if(impersonate_role === 'Driver'){
      profile.userRoles = ['cvv_driver'];
    }
    if(impersonate_role === 'View'){
      profile.userRoles = ['cvv_view_only'];
    }
    if(impersonate_role === 'None'){
      profile.userRoles = [];
    }
    return profile
  }

  private handleError(error: HttpErrorResponse) {
    // this.router.navigateByUrl("/error");
    //Bug 7093024: skip generic toast for 409 so component shows specific van-conflict message
    if (error.status !== 409) {
      this.toastService.show('HTTP Error Occurred!', { classname: 'bg-danger text-light', delay: 8000 });
    }
    const errorBody = this.stringifyErrorBody(error.error);
    this.logger.error(
      `Profile Service error occurred: status=${error.status}, url=${error.url || 'unknown'}, message=${error.message}, body=${errorBody}`
    );
    return throwError(() => error);
  }

  private stringifyErrorBody(errorBody: any): string {
    if (errorBody === null || errorBody === undefined) {
      return 'null';
    }

    if (typeof errorBody === 'string') {
      return errorBody;
    }

    try {
      return JSON.stringify(errorBody);
    } catch {
      return String(errorBody);
    }
  }

  makeAccountName(profile: Profile): string {
    return `${profile.firstName[0].toUpperCase() || ''}. ${profile.lastName || ''}`;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  saveProfile(profileUpdate: ProfileUpdate): Observable<HttpResponse<any>> {
    let _profileUpdate = JSON.parse(JSON.stringify(profileUpdate));
    _profileUpdate.vanBgColorPref = profileUpdate['vanBgColorPref']['value'].substring(0, 7);
    _profileUpdate.vanFontColorPref = profileUpdate['vanFontColorPref']['value'].substring(0, 7);
    const config = this.configurationService.configuration$.getValue();
    if (!config) {
      return throwError(new Error('Configuration not loaded'));
    }
    const url = config.HostApi.concat('/asset');
    const headers = { 'Content-type': 'application/json; charset=utf-8' };
    return this.http.put<any>(url, _profileUpdate, { headers, observe: 'response' }).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  static get servicePrincipalToken() {
    return window.sessionStorage.getItem(MsCal.MSAL_SERVICE_SS_KEY);
  }

}
